import {
  createColumnHelper, flexRender, getCoreRowModel,
  getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import Searchbox from "components/fields/Searchbox";
import { KycRowObj } from "..";
import moment from "moment";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import Pagination from "components/pagination";
import InfoModal from "./InfoModal";
import { toast } from "react-hot-toast";
import CustomToolTip from "components/tooltips/new";
import CreateKycVelocityModal from "./CreateKycVelocityModal";
import DeleteModal from "./DeleteKycVelocityModal";
import { deleteKycVelocity } from "api/graphQL_kycvelocity";

const columnHelper = createColumnHelper<KycRowObj>();

function KycTable(props: {
  tableData: any; fetchKycVelocity: () => void; onValueChange: (value: string) => void;
  roleData?: any; isLoading: boolean; page: number; setPage: any;
  totalpage: number; totalItems: number; currentPage: number;
  pageSize: number; setPageSize: any; clients: any;
  filterData: () => void; filter: any; setFilter: (e: any) => void;
  setLocalFilters?: (e: any) => void; setClearClick: () => void; localFilters?: any;
}) {
  const { tableData, onValueChange, isLoading, roleData, page, setPage, totalpage, totalItems, currentPage, pageSize, setPageSize, fetchKycVelocity } = props;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);

  useEffect(() => { setData(tableData?.map((item: any) => ({ ...item })) ?? []); }, [tableData]);

  const handleDelete = (deleteId: string) => {
    deleteKycVelocity(deleteId)
      .then((d) => { fetchKycVelocity(); toast.success(d?.message || "Record deleted"); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Deletion failed"));
  };

  const parsePaymentMethods = (raw: any) => {
    if (typeof raw === "string") { try { return JSON.parse(raw); } catch { return {}; } }
    return raw ?? {};
  };

  const hasActions = roleData?.[0]?.kyc_velocity?.value?.update_kyc_velocity ||
    roleData?.[0]?.kyc_velocity?.value?.view_kyc_velocity_details ||
    roleData?.[0]?.kyc_velocity?.value?.delete_kyc_velocity;

  const baseColumns = [
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => <span className="kyct-th">Created Date</span>,
      cell: (info: any) => (
        <div className="kyct-date-cell">
          <div className="kyct-date-icon">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span className="kyct-date">{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor("country", {
      id: "country",
      header: () => <span className="kyct-th">Country</span>,
      cell: (info: any) => (
        <div className="kyct-country-cell">
          <div className="kyct-country-flag">{info?.getValue()?.substring(0, 2)}</div>
          <span className="kyct-country-name">{info?.getValue()}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.country || "";
        const b = rowB.original.country || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
      enableSorting: true,
    }),
    // columnHelper.accessor("transactionLimit", {
    //   id: "transactionLimit",
    //   header: () => <span className="kyct-th">Velocity Data</span>,
    //   cell: (info: any) => (
    //     <InfoModal info={info.row.original} type="request" />
    //   ),
    //   enableSorting: false,
    // }),
    columnHelper.accessor("paymentMethods", {
      id: "paymentMethods",
      header: () => <span className="kyct-th">Payment Methods</span>,
      cell: (info: any) => {
        const pm = parsePaymentMethods(info?.row?.original?.paymentMethods);
        const trueKeys = Object.keys(pm).filter((k) => pm[k]);
        const trueCount = trueKeys.length;
        return (
          <div>
            <CustomToolTip
              content={
                trueCount > 0 ? (
                    <div className="ct-gw-tooltip-grid">
                        {trueKeys.map((meth: any,m:any) => (
                            <span key={m} className="ct-gw-chip">
                            {meth.replace("_", " ")}
                            </span>
                        ))}
                    </div>
                ) : <span className="ct-no-gw">No gateways assigned</span>
              }
              trigger={
                <div
                    className={`ct-gw-pill ${
                    trueCount === 0 ? "ct-gw-pill--empty" : ""
                    }`}
                >
                    <span className="ct-gw-dot" />
                    <span>{trueCount}</span>

                    {trueCount > 0 && (
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        fill="none"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                    )}
                </div>
              }
              header="Payment Methods"
            />
          </div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const countA = Object.values(parsePaymentMethods(rowA.original.paymentMethods)).filter(Boolean).length;
        const countB = Object.values(parsePaymentMethods(rowB.original.paymentMethods)).filter(Boolean).length;
        const result = countA - countB;
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(
          String(rowB.original.id)
        );
      },
      enableSorting: true,
    }),
  ];

  const actionColumn = columnHelper.accessor("velocityId", {
    id: "velocityId",
    header: () => <span className="kyct-th">Actions</span>,
    cell: (info: any) => (
      <div className="kyct-actions">
        {roleData?.[0]?.kyc_velocity?.value?.update_kyc_velocity && (
          <CreateKycVelocityModal
            fetchKycVelocity={fetchKycVelocity}
            id={info.getValue()}
            velocityId={info?.row?.original?.velocityId}
            roleData={roleData} info={false} infoData={info}
          />
        )}
        {roleData?.[0]?.kyc_velocity?.value?.view_kyc_velocity_details && (
          <InfoModal info={info.row.original} type="request" />
          // <CreateKycVelocityModal
          //   fetchKycVelocity={fetchKycVelocity}
          //   id={info.getValue()}
          //   velocityId={info?.row?.original?.velocityId}
          //   roleData={roleData} info={true} infoData={info}
          // />
        )}
        {roleData?.[0]?.kyc_velocity?.value?.delete_kyc_velocity && (
          <DeleteModal
            fetchKycVelocity={fetchKycVelocity}
            id={info.getValue()}
            name={info?.row?.original?.first_name ?? ""}
            heading="Delete Record"
            infoData={info}
            message="This will permanently delete the selected KYC velocity record. Do you want to continue?"
            handleDelete={handleDelete}
          />
        )}
      </div>
    ),
    enableSorting: false,
  });

  const table = useReactTable({
    data,
    columns: hasActions ? [...baseColumns, actionColumn] : baseColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="kyct-card">
      {/* Header */}
      <div className="kyct-header">
        <div className="kyct-header-left">
          <div className="kyct-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div>
            <h3 className="kyct-title">KYC Velocity Rules</h3>
            <p className="kyct-subtitle">{totalItems > 0 ? `${totalItems} rule${totalItems !== 1 ? "s" : ""}` : "No rules configured"}</p>
          </div>
        </div>
        <Searchbox onSearch={(e) => onValueChange(e)} />
      </div>

      {/* Table */}
      <div className="kyct-table-wrap">
        {isLoading ? (
          <div className="kyct-loading"><div className="kyct-spinner" /><p className="kyct-loading-txt">Loading KYC rules…</p></div>
        ) : (
          <table className="kyct-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`kyct-thead-th${h.column.getCanSort() ? " kyct-thead-th--sort" : ""}`}
                    >
                      <div className="kyct-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="kyct-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr key={row.id} className={`kyct-tbody-tr${ri % 2 !== 0 ? " kyct-tbody-tr--alt" : ""}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="kyct-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr><td colSpan={6}>
                  <div className="kyct-empty">
                    <div className="kyct-empty-icon">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <p className="kyct-empty-title">No KYC velocity rules</p>
                    <p className="kyct-empty-sub">Add your first rule using the button above</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="kyct-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .ct-gw-tooltip-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
          .ct-gw-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 9px;
          font-size: 12px;
          font-weight: 700;

          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          border: 1.5px solid #bfdbfe;
          color: #1d4ed8;

          transition: 0.15s ease;
          cursor: pointer;
        }

        .ct-gw-pill:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }

        .ct-gw-pill--empty {
          background: #f8fafc;
          border-color: #e2e8f0;
          color: #94a3b8;
          cursor: default;
        }
          .ct-gw-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #2563eb;
        }
        .ct-no-gw { font-size: 12px; color: #94a3b8; font-style: italic; }
        .ct-gw-chip {
          font-size: 11.5px;
          font-weight: 600;

          padding: 4px 8px;
          border-radius: 6px;

          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #374151;
          text-transform: capitalize;
        }
        .ct-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }
        .kyct-card { font-family:'Plus Jakarta Sans',sans-serif; background:#fff; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,0.06); }

        .kyct-header { padding:20px 24px 16px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .kyct-header-left { display:flex; align-items:center; gap:12px; }
        .kyct-header-icon { width:40px; height:40px; border-radius:11px; background:linear-gradient(135deg,#fdf4ff,#fae8ff); border:1.5px solid #e9d5ff; display:flex; align-items:center; justify-content:center; color:#7c3aed; flex-shrink:0; }
        .kyct-title { font-size:15px; font-weight:700; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:22px; }
        .kyct-subtitle { font-size:12px; color:#94a3b8; font-weight:500; }

        .kyct-table-wrap { overflow-x:auto; }
        .kyct-table-wrap::-webkit-scrollbar { height:4px; }
        .kyct-table-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .kyct-table { width:100%; border-collapse:collapse; }

        .kyct-thead-th { padding:12px 16px 10px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; user-select:none; }
        .kyct-thead-th--sort { cursor:pointer; }
        .kyct-thead-th--sort:hover { background:#f1f5f9; }
        .kyct-th-inner { display:flex; align-items:center; gap:5px; }
        .kyct-th { font-size:11.5px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .kyct-sort-icon { color:#94a3b8; display:flex; align-items:center; }
        .kyct-thead-th--sort:hover .kyct-sort-icon { color:#7c3aed; }

        .kyct-tbody-tr { transition:background .12s; }
        .kyct-tbody-tr:hover { background:#fdf4ff; }
        .kyct-tbody-tr--alt { background:#fafbfc; }
        .kyct-tbody-tr--alt:hover { background:#faf5ff; }
        .kyct-tbody-td { padding:12px 16px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }

        .kyct-date-cell { display:flex; align-items:center; gap:8px; }
        .kyct-date-icon { width:24px; height:24px; border-radius:6px; background:linear-gradient(135deg,#fdf4ff,#fae8ff); border:1.5px solid #e9d5ff; display:flex; align-items:center; justify-content:center; color:#7c3aed; flex-shrink:0; }
        .kyct-date { font-size:12.5px; font-weight:600; color:#0f172a; white-space:nowrap; }

        .kyct-country-cell { display:flex; align-items:center; gap:8px; }
        .kyct-country-flag { width:28px; height:28px; border-radius:7px; flex-shrink:0; background:linear-gradient(135deg,#fdf4ff,#fae8ff); border:1.5px solid #e9d5ff; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; color:#7c3aed; }
        .kyct-country-name { font-size:13px; font-weight:700; color:#0f172a; }

        .kyct-pm-badge { display:inline-flex; align-items:center; padding:4px 12px; border-radius:8px; font-size:12px; font-weight:700; background:linear-gradient(135deg,#fdf4ff,#fae8ff); border:1.5px solid #e9d5ff; color:#7c3aed; cursor:default; }

        .kyct-actions { display:flex; align-items:center; gap:6px; }

        .kyct-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; gap:10px; }
        .kyct-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .kyct-empty-title { font-size:14px; font-weight:700; color:#64748b; }
        .kyct-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        .kyct-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px; gap:12px; }
        .kyct-spinner { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#7c3aed; border-radius:50%; animation:kyctSpin .7s linear infinite; }
        @keyframes kyctSpin { to { transform:rotate(360deg); } }
        .kyct-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }

        .kyct-pagination-wrap { padding:12px 16px; border-top:1px solid #f1f5f9; background:#fafbfc; }

        @media (max-width:700px) { .kyct-header { flex-direction:column; align-items:flex-start; } }
      `}</style>
    </div>
  );
}

export default KycTable;